import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Crown } from 'lucide-react';
import { limitValueLabel } from '../../lib/planProjection';

// Presentation-only Billing current-plan card. It receives the ALREADY-resolved canonical projection through
// props — `plan` = selectCurrentPlan(entitlements), `planLimits` = normalizeLimits(entitlements) — and renders
// it. It performs NO data fetching, NO Firebase/auth, NO authedFetch, NO localStorage, NO Checkout/portal/plan
// mutation, and NEVER interprets raw catalogue tiers as the current plan. Billing.jsx owns fetching, auth,
// loading/error orchestration and the canonical selection; this component only displays what it is given.
const _ASSIGNMENT_LABEL = {
  paid_subscription: 'Paid subscription',
  grandfathered: 'Grandfathered plan',
  manual_or_custom_contract: 'Manual / custom contract',
  checkout_pending: 'Checkout pending',
  assigned_unpaid: 'Assigned — billing not active',
};
const fmtMoney = (amount, currency = 'usd') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: (currency || 'usd').toUpperCase() }).format(amount || 0);

export default function BillingCurrentPlanCard({
  plan, planLimits = [], loading = false, status = '', trialDaysLeft = null, billingState = null, t = (k) => k,
}) {
  return (
    <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center"><Crown className="h-5 w-5 mr-2 text-amber-500" />{t('pg.billing.currentPlan')}</CardTitle>
        <CardDescription>{t('pg.billing.currentPlanDesc')}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-4 gap-4 text-sm">
          <div className="p-4 rounded-lg bg-purple-50 border border-purple-100">
            <div className="text-gray-500">{t('pg.billing.tier')}</div>
            <div className="font-semibold capitalize text-purple-700 text-lg" data-testid="current-tier-name">
              {plan?.display_name || (loading ? '…' : '—')}
            </div>
            {plan && (
              <div className="mt-1 flex flex-wrap items-center gap-2">
                {plan.version && (
                  <span className="text-xs text-purple-500" data-testid="current-plan-version">{plan.version}</span>
                )}
                <Badge className={plan.grandfathered ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-700'} data-testid="current-plan-badge">
                  {plan.grandfathered ? 'Grandfathered' : 'Active'}
                </Badge>
              </div>
            )}
          </div>
          <div className="p-4 rounded-lg bg-green-50 border border-green-100">
            <div className="text-gray-500">{t('pg.billing.price')}</div>
            <div className="font-semibold text-green-700 text-lg" data-testid="current-plan-price">
              {plan
                ? (plan.price_cents == null
                    ? t('pg.billing.onRequest')
                    : `${fmtMoney((plan.price_cents || 0) / 100)}/${(plan.cadence || 'monthly').replace(/ly$/, '')}`)
                : (loading ? '…' : '—')}
            </div>
          </div>
          <div className="p-4 rounded-lg bg-amber-50 border border-amber-100">
            <div className="text-gray-500">{t('pg.billing.status')}</div>
            <div className="font-semibold text-amber-700 capitalize">{(status || '').replace('_', ' ')}</div>
            {trialDaysLeft !== null && (
              <div className="text-xs text-amber-600 mt-1" data-testid="trial-days-left">
                {trialDaysLeft} day{trialDaysLeft === 1 ? '' : 's'} left in trial
              </div>
            )}
          </div>
        </div>

        {billingState && (
          <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm" data-testid="billing-state-panel">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className={billingState.billing_provider_status === 'active_paid' ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}>
                {billingState.billing_provider_status === 'active_paid' ? 'Active paid base-plan subscription' : 'No active paid base-plan subscription'}
              </Badge>
              <Badge className="bg-blue-100 text-blue-800">{_ASSIGNMENT_LABEL[billingState.plan_assignment_type] || billingState.plan_assignment_type}</Badge>
              {billingState.grandfathered && <Badge className="bg-amber-100 text-amber-800">Grandfathered</Badge>}
              {billingState.manual_contract && <Badge className="bg-purple-100 text-purple-800">Manual / custom contract</Badge>}
              {billingState.checkout_pending && <Badge className="bg-yellow-100 text-yellow-800">Checkout pending</Badge>}
              {billingState.cancellation_state === 'cancelled' && <Badge className="bg-red-100 text-red-700">Cancelled</Badge>}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {billingState.manual_contract
                ? 'Enterprise custom/manual agreement — there is no Stripe subscription. Contact sales to change terms.'
                : billingState.billing_provider_status === 'active_paid'
                  ? (billingState.next_billing_date ? `Your paid subscription is active. Next billing: ${billingState.next_billing_date}` : 'Your paid subscription is active.')
                  : billingState.checkout_required
                    ? 'Billing is not yet active for this plan — use Activate billing to complete checkout.'
                    : 'Billing status is up to date.'}
              {billingState.current_plan_version ? ` · Plan version: ${billingState.current_plan_version}` : ''}
            </p>
          </div>
        )}

        {/* REAL limits from the entitlements projection. "Unlimited" ONLY when the canonical value is -1; a
            missing limit renders "Unavailable" — never inferred from a plan name or the tier catalogue. */}
        {planLimits.length > 0 ? (
          <div className="grid md:grid-cols-3 gap-3 text-sm mt-4" data-testid="entitlement-limits">
            {planLimits.map((l) => (
              <div key={l.limit_key} className="border rounded-lg p-3" data-testid={`entitlement-limit-${l.limit_key}`}>
                <div className="text-gray-500 capitalize">{l.display_name}</div>
                <div className="font-semibold">{limitValueLabel(l)}</div>
                {/* Truthful provenance: an effective allowance set by a tenant Override (or an Add-on) must NOT read
                    as a base-plan inclusion. Show the canonical allowance_source when the projection provides it. */}
                {l.allowance_source ? (
                  <div className="text-[11px] text-gray-400 mt-0.5" data-testid={`entitlement-limit-${l.limit_key}-source`}>
                    Source: {String(l.allowance_source).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-gray-400 mt-4" data-testid="entitlement-limits-loading">
            {loading ? 'Loading plan limits…' : 'Plan limits are momentarily unavailable.'}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
